import {
  SettingsCard,
  SettingsDialog,
  SettingsError,
  SettingsSection,
} from "@/components/SettingsUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type MintServiceTokenBody,
  type MintServiceTokenResponse,
  type ServiceTokenRecord,
  useActiveOrg,
  useApiMutation,
  useApiQuery,
} from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Check, Copy, KeyRound, LockKeyhole, Plus, Search, ShieldCheck } from "lucide-react";
import { RadioGroup } from "radix-ui";
import { useId, useRef, useState } from "react";

type Role = MintServiceTokenBody["role"];
const roles: { value: Role; label: string; description: string }[] = [
  { value: "viewer", label: "Viewer", description: "Read secrets allowed by policy." },
  {
    value: "developer",
    label: "Developer",
    description: "Read, write, and rotate secrets. Suitable for CI.",
  },
  { value: "admin", label: "Admin", description: "Full access except organization deletion." },
  {
    value: "owner",
    label: "Owner",
    description: "Full organization access. Use only when necessary.",
  },
];

export function SettingsServiceTokensRoute() {
  const org = useActiveOrg();
  return <ServiceTokensWorkspace key={org.org_id} />;
}

function ServiceTokensWorkspace() {
  const org = useActiveOrg();
  const canManage = org.role === "owner" || org.role === "admin";
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<MintServiceTokenResponse | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useApiQuery(
    ["service-tokens", org.org_id],
    (client) => client.serviceTokens.list(org.org_id),
    { suppressToast: true, enabled: canManage },
  );
  const tokens = list.data?.tokens ?? [];
  const filtered = tokens.filter(
    (token) =>
      token.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()) &&
      (status === "all" || tokenStatus(token) === status),
  );
  return (
    <SettingsSection
      title="Service tokens"
      description="Give your applications and automations access to Wardkey."
      icon={KeyRound}
      action={
        <Button
          ref={trigger}
          type="button"
          size="sm"
          disabled={!canManage}
          onClick={() => setCreating(true)}
        >
          <Plus size={15} aria-hidden="true" />
          Create token
        </Button>
      }
    >
      {!canManage ? (
        <SettingsCard title="Managed by your administrators">
          <div className="settings-info-row">
            <LockKeyhole size={22} aria-hidden="true" />
            <div>
              <strong>Owner or admin access required</strong>
              <p>Ask an organization owner or admin to view, create, or revoke service tokens.</p>
            </div>
          </div>
        </SettingsCard>
      ) : list.error ? (
        <SettingsError error={list.error} retry={list.refetch} />
      ) : list.isLoading || !list.data ? (
        <div
          className="settings-card settings-loading"
          aria-busy="true"
          aria-label="Loading service tokens"
        >
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <>
          <div className="settings-token-toolbar">
            <div className="settings-token-search">
              <Search size={16} aria-hidden="true" />
              <input
                aria-label="Find a service token"
                placeholder="Find a token…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <select
              aria-label="Token status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
          {tokens.length === 0 ? (
            <div className="settings-empty">
              <span className="settings-section-icon">
                <KeyRound size={25} aria-hidden="true" />
              </span>
              <h3>No service tokens</h3>
              <p>
                Create a token for each application or pipeline that needs access to your secrets.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="settings-empty">
              <Search size={24} aria-hidden="true" />
              <h3>No matching tokens</h3>
              <p>Try a different name or status.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setQuery("");
                  setStatus("all");
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="settings-token-list">
              <div className="settings-token-columns" aria-hidden="true">
                <span>Token / access</span>
                <span>Activity</span>
                <span>Status</span>
                <span />
              </div>
              <ul>
                {filtered.map((token) => (
                  <TokenRow
                    key={token.token_id}
                    token={token}
                    orgId={org.org_id}
                    onRevoked={list.refetch}
                  />
                ))}
              </ul>
              <div className="settings-token-count">
                {filtered.length} of {tokens.length} tokens
              </div>
            </div>
          )}
          <div className="settings-caption">
            <ShieldCheck size={16} aria-hidden="true" />
            <p>
              Use the minimum access each application needs. Token values are shown only when
              created.
            </p>
          </div>
        </>
      )}
      {creating && (
        <CreateTokenDialog
          orgId={org.org_id}
          returnFocus={trigger}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setCreated(result);
            list.refetch();
          }}
        />
      )}
      {created && (
        <TokenReveal result={created} returnFocus={trigger} onClose={() => setCreated(null)} />
      )}
    </SettingsSection>
  );
}

function tokenStatus(token: ServiceTokenRecord) {
  return token.revoked_at !== null
    ? "revoked"
    : token.expires_at !== null && token.expires_at <= Date.now()
      ? "expired"
      : "active";
}
function dateLabel(timestamp: number) {
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function TokenRow({
  token,
  orgId,
  onRevoked,
}: { token: ServiceTokenRecord; orgId: string; onRevoked: () => void }) {
  const { pushToast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);
  const status = tokenStatus(token);
  const revoke = useApiMutation<void, void>(
    (client) => client.serviceTokens.revoke(orgId, token.token_id),
    {
      suppressToast: true,
      onSuccess: () => {
        setConfirming(false);
        pushToast({ kind: "success", title: `Revoked ${token.label}` });
        onRevoked();
      },
      onError: (err) => setError(err.message),
    },
  );
  return (
    <li className="settings-token-row">
      <div className="settings-token-name">
        <span className="settings-token-icon">
          <KeyRound size={17} aria-hidden="true" />
        </span>
        <div>
          <strong title={token.label}>{token.label}</strong>
          <span aria-label={`role: ${token.role}`} className="settings-token-role">
            {token.role}
          </span>
          <small>
            {token.expires_at === null ? "No expiry" : `Expires ${dateLabel(token.expires_at)}`}
          </small>
        </div>
      </div>
      <div className="settings-token-activity">
        <span>Created {dateLabel(token.created_at)}</span>
        <small>
          {token.last_used_at === null
            ? "Not used yet"
            : `Last used ${dateLabel(token.last_used_at)}`}
        </small>
      </div>
      <span className={`settings-token-status ${status}`}>
        <span aria-hidden="true" />
        {status}
      </span>
      <div className="settings-token-actions">
        {status !== "revoked" && (
          <Button
            ref={trigger}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
          >
            Revoke
          </Button>
        )}
      </div>
      {confirming && (
        <SettingsDialog
          title="Revoke this token?"
          description={`Applications using ${token.label} will lose access on their next request. This cannot be undone.`}
          onClose={() => setConfirming(false)}
          busy={revoke.isPending}
          returnFocus={trigger}
        >
          <div className="settings-token-confirm">
            <KeyRound size={20} aria-hidden="true" />
            <strong>{token.label}</strong>
          </div>
          {error && (
            <p className="settings-inline-error" role="alert">
              {error}
            </p>
          )}
          <div className="settings-dialog-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirming(false)}
              disabled={revoke.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (!revoke.isPending) revoke.mutate();
              }}
              disabled={revoke.isPending}
            >
              {revoke.isPending ? "Revoking…" : "Confirm revocation"}
            </Button>
          </div>
        </SettingsDialog>
      )}
    </li>
  );
}

function CreateTokenDialog({
  orgId,
  onClose,
  onCreated,
  returnFocus,
}: {
  orgId: string;
  onClose: () => void;
  onCreated: (result: MintServiceTokenResponse) => void;
  returnFocus: React.RefObject<HTMLButtonElement | null>;
}) {
  const [label, setLabel] = useState("");
  const [role, setRole] = useState<Role>("developer");
  const [days, setDays] = useState("");
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const mint = useApiMutation<MintServiceTokenBody, MintServiceTokenResponse>(
    (client, body) => client.serviceTokens.mint(orgId, body),
    { suppressToast: true, onSuccess: onCreated, onError: (err) => setError(err.message) },
  );
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (mint.isPending) return;
    setError(null);
    if (!label.trim() || label.trim().length > 128) {
      setError("Enter a label of 1–128 characters.");
      return;
    }
    let expires_at: number | null = null;
    if (days.trim()) {
      const count = Number(days);
      const expiry = Date.now() + count * 86_400_000;
      if (
        !Number.isSafeInteger(count) ||
        count <= 0 ||
        !Number.isSafeInteger(expiry) ||
        expiry > 8_640_000_000_000_000
      ) {
        setError("Expiry must be a valid, positive whole number of days.");
        return;
      }
      expires_at = expiry;
    }
    mint.mutate({ label: label.trim(), role, expires_at });
  }
  return (
    <SettingsDialog
      title="Create service token"
      description="Choose a name and the access this application needs."
      onClose={onClose}
      busy={mint.isPending}
      returnFocus={returnFocus}
    >
      <form onSubmit={submit}>
        <fieldset disabled={mint.isPending} className="settings-token-fields">
          <div className="settings-field">
            <label htmlFor={`${id}-label`}>Token label</label>
            <Input
              id={`${id}-label`}
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
                setError(null);
              }}
              placeholder="gha-prod-deploy"
              maxLength={128}
              autoComplete="off"
              spellCheck={false}
            />
            <p>A recognizable name, such as your application or deployment.</p>
          </div>
          <div className="settings-field">
            <p id={`${id}-role`} className="settings-field-label">
              Access level
            </p>
            <RadioGroup.Root
              value={role}
              onValueChange={(value) => setRole(value as Role)}
              aria-labelledby={`${id}-role`}
              className="settings-role-options"
              disabled={mint.isPending}
            >
              {roles.map((option) => (
                <RadioGroup.Item
                  key={option.value}
                  value={option.value}
                  className="settings-role-option"
                >
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                  <span className="settings-role-indicator">
                    <RadioGroup.Indicator>
                      <Check size={12} />
                    </RadioGroup.Indicator>
                  </span>
                </RadioGroup.Item>
              ))}
            </RadioGroup.Root>
          </div>
          <div className="settings-field">
            <label htmlFor={`${id}-expiry`}>Expires in (days)</label>
            <Input
              id={`${id}-expiry`}
              type="text"
              inputMode="numeric"
              value={days}
              onChange={(event) => {
                setDays(event.target.value);
                setError(null);
              }}
              placeholder="No expiry"
            />
            <p>Leave blank for a token that does not expire.</p>
          </div>
        </fieldset>
        {error && (
          <p className="settings-inline-error" role="alert">
            {error}
          </p>
        )}
        <div className="settings-dialog-actions">
          <Button type="button" variant="ghost" disabled={mint.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mint.isPending || !label.trim()}>
            {mint.isPending ? "Creating…" : "Create token"}
          </Button>
        </div>
      </form>
    </SettingsDialog>
  );
}

function TokenReveal({
  result,
  onClose,
  returnFocus,
}: {
  result: MintServiceTokenResponse;
  onClose: () => void;
  returnFocus: React.RefObject<HTMLButtonElement | null>;
}) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function copy() {
    try {
      await navigator.clipboard.writeText(result.token);
      setCopied(true);
      setError(null);
    } catch {
      setError("Couldn't copy. Select and copy the token below instead.");
    }
  }
  return (
    <SettingsDialog
      title="Save your token"
      description="This token is shown only once. Store it somewhere secure before closing this window."
      onClose={onClose}
      deliberateClose
      returnFocus={returnFocus}
    >
      <div className="settings-token-confirm">
        <KeyRound size={20} aria-hidden="true" />
        <strong>{result.record.label}</strong>
      </div>
      <div className="settings-token-value" data-testid="service-token-plaintext">
        {result.token}
      </div>
      {error && (
        <p className="settings-inline-error" role="alert">
          {error}
        </p>
      )}
      <div className="settings-dialog-actions">
        <Button type="button" variant="outline" onClick={copy}>
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
          {copied ? "Copied" : "Copy token"}
        </Button>
        <Button type="button" onClick={onClose}>
          I've saved this token
        </Button>
      </div>
    </SettingsDialog>
  );
}
